import { c as _c } from "react/compiler-runtime";
import React, { useState, useEffect } from "react";
import { EffektCheckbox } from "../../../../shared/components/EffektCheckbox/EffektCheckbox";
import { LinkComponent } from "../../Links/Links";
import { NavLink } from "../../../../shared/components/Navbar/Navbar";
import { Spinner } from "../../../../shared/components/Spinner/Spinner";
import { PaymentMethodSelector } from "../components/PaymentMethodSelector";
import { FormData } from "../hooks/useFundraiserForm";
import styles from "../FundraiserWidget.module.scss";
import Link from "next/link";
import { validateOrg, validateSsn } from "@ssfbank/norwegian-id-validators";
import Organisationsnummer from "organisationsnummer";
import Personnummer from "personnummer";
import { validateTin, formatTinInput } from "../../../../../util/tin-validation";
import { FormattingLocale } from "../../../../../util/formatting";
import { fundraiserSubmitButtonText } from "../fundraiserSubmitButtonText";
interface PaymentMethodPaneProps {
  formData: Pick<FormData, "taxDeduction" | "newsletter" | "email" | "ssn" | "paymentMethod" | "privacyPolicyAccepted">;
  onChange: (field: keyof FormData, value: any) => void;
  onSubmit: () => void;
  loading: boolean;
  config: {
    allow_anonymous_donations: boolean;
    tax_deduction?: {
      label: string;
      tooltip_text: string;
      ssn_label: string;
      ssn_invalid_error_text: string;
      ssn_suspicious_error_text: string;
    } | null;
    newsletter?: {
      label: string;
    } | null;
    email_label: string;
    privacy_policy: {
      text: string;
      require_checkbox?: boolean;
      required_error_text?: string;
      privacy_policy_url?: NavLink;
    };
    payment_methods: Array<{
      _type: string;
      selector_text: string | null;
      single_button_text: string | null;
      recurring_button_text: string | null;
      button_text: string | null;
    }>;
  };
  className?: string;
  visible?: boolean;
  privacyPolicyError?: boolean;
  locale: FormattingLocale;
}
const validateSsnNo = (ssn: string): boolean => {
  return ssn.length === 9 && validateOrg(ssn) || ssn.length === 11 && validateSsn(ssn);
};
const validateSsnSe = (ssn: string): boolean => {
  return Personnummer.valid(ssn) || Organisationsnummer.valid(ssn);
};
export const PaymentMethodPane = React.forwardRef<HTMLDivElement, PaymentMethodPaneProps>((t0, ref) => {
  const $ = _c(80);
  const {
    formData,
    onChange,
    onSubmit,
    loading,
    config,
    className,
    visible,
    privacyPolicyError,
    locale
  } = t0;
  const [ssnError, setSsnError] = useState(null);
  const [cprSuspicious, setCprSuspicious] = useState(false);
  let t1;
  let t2;
  if ($[0] !== formData.taxDeduction) {
    t1 = () => {
      if (!formData.taxDeduction) {
        setSsnError(null);
        setCprSuspicious(false);
      }
    };
    t2 = [formData.taxDeduction];
    $[0] = formData.taxDeduction;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== config.tax_deduction?.ssn_invalid_error_text || $[4] !== formData.taxDeduction || $[5] !== locale || $[6] !== onChange) {
    t3 = e => {
      const {
        value
      } = e.target;
      let valueToUse = value;
      if (locale === "da-DK" && formData.taxDeduction) {
        const formattedValue = formatTinInput(value, {
          allowCvr: true
        });
        e.target.value = formattedValue;
        valueToUse = formattedValue;
        onChange("ssn", formattedValue);
      } else {
        onChange("ssn", value);
      }
      const trimmed = valueToUse.trim();
      if (!formData.taxDeduction || trimmed === "") {
        setSsnError(null);
        setCprSuspicious(false);
        return;
      }
      let isValid = true;
      if (locale === "no-NB") {
        isValid = validateSsnNo(trimmed);
      } else {
        if (locale === "sv-SE") {
          isValid = validateSsnSe(trimmed);
        } else {
          if (locale === "da-DK") {
            const validationResult = validateTin(trimmed, {
              allowCvr: true
            });
            if (validationResult.type === "CPR") {
              if (validationResult.isSuspicious) {
                setCprSuspicious(true);
              } else {
                if (validationResult.isValid && !validationResult.isSuspicious) {
                  setCprSuspicious(false);
                }
              }
            } else {
              setCprSuspicious(false);
            }
            isValid = validationResult.isValid;
          }
        }
      }
      setSsnError(isValid ? null : config.tax_deduction?.ssn_invalid_error_text || null);
    };
    $[3] = config.tax_deduction?.ssn_invalid_error_text;
    $[4] = formData.taxDeduction;
    $[5] = locale;
    $[6] = onChange;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleSsnChange = t3;
  let t4;
  if ($[8] !== config.tax_deduction?.ssn_invalid_error_text || $[9] !== formData.ssn || $[10] !== formData.taxDeduction || $[11] !== locale) {
    t4 = () => {
      const trimmed_0 = formData.ssn.trim();
      if (!formData.taxDeduction || trimmed_0 === "") {
        setSsnError(null);
        setCprSuspicious(false);
        return;
      }
      let isValid_0 = true;
      if (locale === "no-NB") {
        isValid_0 = validateSsnNo(trimmed_0);
      } else {
        if (locale === "sv-SE") {
          isValid_0 = validateSsnSe(trimmed_0);
        } else {
          if (locale === "da-DK") {
            const validationResult_0 = validateTin(trimmed_0, {
              allowCvr: true
            });
            if (validationResult_0.type === "CPR") {
              if (validationResult_0.isSuspicious) {
                setCprSuspicious(true);
              } else {
                if (validationResult_0.isValid && !validationResult_0.isSuspicious) {
                  setCprSuspicious(false);
                }
              }
            } else {
              setCprSuspicious(false);
            }
            isValid_0 = validationResult_0.isValid;
          }
        }
      }
      setSsnError(isValid_0 ? null : config.tax_deduction?.ssn_invalid_error_text || null);
    };
    $[8] = config.tax_deduction?.ssn_invalid_error_text;
    $[9] = formData.ssn;
    $[10] = formData.taxDeduction;
    $[11] = locale;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const validateSsnOnBlur = t4;
  let t5;
  if ($[13] !== onChange) {
    t5 = e_0 => onChange("email", e_0.target.value);
    $[13] = onChange;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const t6 = locale === "da-DK" || formData.taxDeduction || formData.newsletter;
  let t7;
  if ($[15] !== config.email_label || $[16] !== formData.email || $[17] !== t5 || $[18] !== t6) {
    t7 = <div className={styles["donation-widget__input-group"]}><input id="email" name="email" type="email" value={formData.email} onChange={t5} required={t6} placeholder={config.email_label} data-cy="fundraiser-email-input" /></div>;
    $[15] = config.email_label;
    $[16] = formData.email;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const emailInput = t7;
  const t8 = visible ? "block" : "none";
  let t9;
  if ($[20] !== t8) {
    t9 = {
      display: t8
    };
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== config.tax_deduction?.ssn_invalid_error_text || $[23] !== formData.ssn || $[24] !== formData.taxDeduction || $[25] !== locale || $[26] !== onSubmit) {
    t10 = e_1 => {
      e_1.preventDefault();
      if (formData.taxDeduction && formData.ssn.trim()) {
        const trimmed_1 = formData.ssn.trim();
        let isValid_1 = true;
        if (locale === "no-NB") {
          isValid_1 = validateSsnNo(trimmed_1);
        } else {
          if (locale === "sv-SE") {
            isValid_1 = validateSsnSe(trimmed_1);
          } else {
            if (locale === "da-DK") {
              const validationResult_1 = validateTin(trimmed_1, {
                allowCvr: true
              });
              isValid_1 = validationResult_1.isValid;
            }
          }
        }
        if (!isValid_1) {
          setSsnError(config.tax_deduction?.ssn_invalid_error_text || null);
          return;
        }
      }
      onSubmit();
    };
    $[22] = config.tax_deduction?.ssn_invalid_error_text;
    $[23] = formData.ssn;
    $[24] = formData.taxDeduction;
    $[25] = locale;
    $[26] = onSubmit;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const t11 = !config.allow_anonymous_donations && emailInput;
  let t12;
  if ($[28] !== config.tax_deduction || $[29] !== formData.taxDeduction || $[30] !== onChange) {
    t12 = config.tax_deduction && <div className={styles["donation-widget__checkbox-group"]}><EffektCheckbox checked={formData.taxDeduction} onChange={checked => {
        onChange("taxDeduction", checked);
      }} dataCy="fundraiser-tax-deduction">{config.tax_deduction.label}</EffektCheckbox><span className={styles["donation-widget__checkbox-group__popup-trigger"]}>?</span>{" "}<div className={styles["donation-widget__checkbox-group__popup"]}>{config.tax_deduction.tooltip_text}</div></div>;
    $[28] = config.tax_deduction;
    $[29] = formData.taxDeduction;
    $[30] = onChange;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== config.newsletter || $[33] !== formData.newsletter || $[34] !== onChange) {
    t13 = config.newsletter && <div className={styles["donation-widget__checkbox-group"]}><EffektCheckbox checked={formData.newsletter} onChange={checked_0 => {
        onChange("newsletter", checked_0);
      }} dataCy="fundraiser-newsletter">{config.newsletter.label}</EffektCheckbox></div>;
    $[32] = config.newsletter;
    $[33] = formData.newsletter;
    $[34] = onChange;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  const t14 = (formData.taxDeduction || formData.newsletter) && config.allow_anonymous_donations && emailInput;
  let t15;
  if ($[36] !== config.tax_deduction || $[37] !== cprSuspicious || $[38] !== formData.ssn || $[39] !== formData.taxDeduction || $[40] !== handleSsnChange || $[41] !== locale || $[42] !== ssnError || $[43] !== validateSsnOnBlur) {
    t15 = formData.taxDeduction && config.tax_deduction && <div className={styles["donation-widget__input-group"]}><input id="ssn" name="ssn" type="text" inputMode="numeric" pattern={locale === "da-DK" ? undefined : "[0-9]*"} value={formData.ssn} onChange={handleSsnChange} onBlur={validateSsnOnBlur} required={formData.taxDeduction} placeholder={config.tax_deduction.ssn_label} autoComplete="off" data-cy="fundraiser-ssn-input" />{ssnError && <div style={{
        color: "var(--primary)",
        fontSize: "14px",
        marginTop: "5px"
      }}>{ssnError}</div>}{cprSuspicious && locale === "da-DK" && <div style={{
        color: "var(--primary)",
        fontSize: "14px",
        marginTop: "5px"
      }}>{config.tax_deduction?.ssn_suspicious_error_text}</div>}</div>;
    $[36] = config.tax_deduction;
    $[37] = cprSuspicious;
    $[38] = formData.ssn;
    $[39] = formData.taxDeduction;
    $[40] = handleSsnChange;
    $[41] = locale;
    $[42] = ssnError;
    $[43] = validateSsnOnBlur;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      marginTop: "2rem"
    };
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== config.privacy_policy.privacy_policy_url || $[47] !== config.privacy_policy.require_checkbox || $[48] !== config.privacy_policy.required_error_text || $[49] !== config.privacy_policy.text || $[50] !== formData.privacyPolicyAccepted || $[51] !== onChange || $[52] !== privacyPolicyError) {
    t17 = <div style={t16}>{config.privacy_policy.require_checkbox ? <><div className={styles["donation-widget__privacy-checkbox-group"]}><div className={styles["donation-widget__checkbox-group"]}><EffektCheckbox checked={formData.privacyPolicyAccepted} onChange={checked_1 => {
              onChange("privacyPolicyAccepted", checked_1);
            }} dataCy="fundraiser-privacy-checkbox">{`${config.privacy_policy.text}`}</EffektCheckbox></div>{config.privacy_policy.privacy_policy_url && <Link href={`/${config.privacy_policy.privacy_policy_url.slug}`} target="_blank">{` ${config.privacy_policy.privacy_policy_url.title} ↗`}</Link>}</div>{privacyPolicyError && <div style={{
          color: "var(--primary)",
          fontSize: "14px",
          marginTop: "5px"
        }}>{config.privacy_policy.required_error_text}</div>}</> : <div className={styles["donation-widget__privacy-link"]}><span>{config.privacy_policy.text}</span>{config.privacy_policy.privacy_policy_url && <Link href={`/${config.privacy_policy.privacy_policy_url.slug}`} target="_blank">{` ${config.privacy_policy.privacy_policy_url.title} ↗`}</Link>}</div>}</div>;
    $[46] = config.privacy_policy.privacy_policy_url;
    $[47] = config.privacy_policy.require_checkbox;
    $[48] = config.privacy_policy.required_error_text;
    $[49] = config.privacy_policy.text;
    $[50] = formData.privacyPolicyAccepted;
    $[51] = onChange;
    $[52] = privacyPolicyError;
    $[53] = t17;
  } else {
    t17 = $[53];
  }
  let t18;
  if ($[54] !== onChange) {
    t18 = method => onChange("paymentMethod", method);
    $[54] = onChange;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  let t19;
  if ($[56] !== config.payment_methods || $[57] !== formData.paymentMethod || $[58] !== t18) {
    t19 = <div className={styles["donation-widget__payment-options"]}><PaymentMethodSelector methods={config.payment_methods} selected={formData.paymentMethod} onSelect={t18} /></div>;
    $[56] = config.payment_methods;
    $[57] = formData.paymentMethod;
    $[58] = t18;
    $[59] = t19;
  } else {
    t19 = $[59];
  }
  let t20;
  if ($[60] !== formData.paymentMethod || $[61] !== loading) {
    t20 = loading ? <Spinner className={styles["donation-widget__spinner"]} /> : fundraiserSubmitButtonText(formData.paymentMethod);
    $[60] = formData.paymentMethod;
    $[61] = loading;
    $[62] = t20;
  } else {
    t20 = $[62];
  }
  let t21;
  if ($[63] !== t20) {
    t21 = <button type="submit" className={styles["donation-widget__button"]} data-cy="fundraiser-submit-button">{t20}</button>;
    $[63] = t20;
    $[64] = t21;
  } else {
    t21 = $[64];
  }
  let t22;
  if ($[65] !== t10 || $[66] !== t11 || $[67] !== t12 || $[68] !== t13 || $[69] !== t14 || $[70] !== t15 || $[71] !== t17 || $[72] !== t19 || $[73] !== t21) {
    t22 = <form className={styles["donation-widget__form"]} data-cy="fundraiser-payment-form" onSubmit={t10}>{t11}{t12}{t13}{t14}{t15}{t17}{t19}{t21}</form>;
    $[65] = t10;
    $[66] = t11;
    $[67] = t12;
    $[68] = t13;
    $[69] = t14;
    $[70] = t15;
    $[71] = t17;
    $[72] = t19;
    $[73] = t21;
    $[74] = t22;
  } else {
    t22 = $[74];
  }
  let t23;
  if ($[75] !== className || $[76] !== ref || $[77] !== t22 || $[78] !== t9) {
    t23 = <div ref={ref} className={className} style={t9} data-cy="fundraiser-pane-payment">{t22}</div>;
    $[75] = className;
    $[76] = ref;
    $[77] = t22;
    $[78] = t9;
    $[79] = t23;
  } else {
    t23 = $[79];
  }
  return t23;
});
PaymentMethodPane.displayName = "PaymentMethodPane";